import React, { useEffect, useState } from "react";
import { api, formatRM } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";
import { CheckCircle2, XCircle, Package, TrendingUp } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ScanHistoryReport } from "@/components/ScanHistoryReport";

export default function Reports() {
  const { hasPerm } = useAuth();
  const canSales = hasPerm("sales");
  const [data, setData] = useState(null);
  const [days, setDays] = useState("30");

  useEffect(() => {
    api.get(`/reports/summary?days=${days}`).then((r) => setData(r.data));
  }, [days]);

  if (!data) return <div className="text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-bold">Reports</h1>
          <p className="text-sm text-muted-foreground">Performance over the last {days} days</p>
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-40" data-testid="report-period"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 days</SelectItem>
            <SelectItem value="30">30 days</SelectItem>
            <SelectItem value="90">90 days</SelectItem>
            <SelectItem value="365">1 year</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard icon={Package} label="Total Jobs" value={data.total_jobs} />
        <MetricCard icon={CheckCircle2} label="Collected" value={data.completed} color="text-emerald-600" />
        <MetricCard icon={XCircle} label="Cancelled" value={data.cancelled} color="text-rose-600" />
        {canSales && <MetricCard icon={TrendingUp} label="Revenue" value={formatRM(data.total_revenue)} color="text-cyan-600" />}
      </div>

      {canSales && <Card className="p-5">
        <h2 className="font-display font-bold mb-4">Daily Revenue (RM)</h2>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={data.daily_revenue}>
            <XAxis dataKey="date" fontSize={11} />
            <YAxis fontSize={11} />
            <Tooltip formatter={(v) => formatRM(v)} />
            <Line type="monotone" dataKey="amount" stroke="#06b6d4" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </Card>}

      <Card className="p-5">
        <h2 className="font-display font-bold mb-4">Staff Performance (Job Count)</h2>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={data.staff_performance}>
            <XAxis dataKey="name" fontSize={11} />
            <YAxis fontSize={11} />
            <Tooltip />
            <Bar dataKey="count" fill="#ec008c" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <ScanHistoryReport days={Number(days)} />
    </div>
  );
}

const MetricCard = ({ icon: Icon, label, value, color = "" }) => (
  <Card className="p-4">
    <Icon className={`w-5 h-5 mb-2 ${color}`} />
    <div className={`text-2xl font-display font-bold font-mono ${color}`}>{value}</div>
    <div className="text-xs text-muted-foreground">{label}</div>
  </Card>
);
